/**
 * Les règles d'un ticket SAV (docs/CADRAGE.md, « Le SAV »).
 *
 * - Un ticket se rattache à un chantier, s'affecte et se planifie comme une étape.
 * - Le partenaire le résout sur place : un rapport et au moins une photo.
 * - Le bureau peut le résoudre lui-même, pour un problème réglé à distance :
 *   le rapport suffit.
 * - Le bureau clôt un ticket résolu, ou le rouvre avec un motif.
 *
 * Fonctions pures : l'accès libre et l'écran les appliquent telles quelles, la
 * base les reprend dans db/007-sav.sql.
 */
import type { PrioriteTicket, StatutTicket, Ticket } from '@/shared/donnees/types';
import type { Verdict } from './chantier';

export const LIBELLE_STATUT_TICKET: Record<StatutTicket, string> = {
  'a-planifier': 'À planifier',
  planifie: 'Planifié',
  'en-cours': 'En cours',
  resolu: 'Résolu',
  clos: 'Clos',
  rouvert: 'Rouvert',
};

export const LIBELLE_PRIORITE: Record<PrioriteTicket, string> = {
  normale: 'Normale',
  urgente: 'Urgente',
};

/** Un ticket qui attend encore quelqu'un : ni résolu, ni clos. */
export function estOuvert(statut: StatutTicket): boolean {
  return statut !== 'resolu' && statut !== 'clos';
}

/** Le statut qu'une affectation donne à un ticket pas encore démarré. */
export function statutTicketApresAffectation(
  statut: StatutTicket,
  affectation: { partenaireId: string | null; debutPrevu: string | null }
): StatutTicket {
  if (statut !== 'a-planifier' && statut !== 'planifie') return statut;
  return affectation.partenaireId !== null && affectation.debutPrevu !== null
    ? 'planifie'
    : 'a-planifier';
}

export function peutAffecterTicket(ticket: Pick<Ticket, 'statut'>): Verdict {
  if (ticket.statut === 'resolu' || ticket.statut === 'clos') {
    return { ok: false, raison: 'Ce ticket est résolu : rouvrez-le pour le replanifier.' };
  }
  return { ok: true };
}

export function peutDemarrerTicket(ticket: Pick<Ticket, 'statut' | 'partenaireId'>): Verdict {
  if (ticket.partenaireId === null) return { ok: false, raison: 'Affectez d’abord un partenaire.' };
  if (ticket.statut === 'a-planifier' || ticket.statut === 'planifie') return { ok: true };
  return { ok: false, raison: 'Cette intervention est déjà démarrée.' };
}

/** Le rapport tient en quelques mots au moins : « RAS » ne dit rien au bureau. */
export const RAPPORT_MIN = 10;
export const RAPPORT_MAX = 4000;

/**
 * Peut-on résoudre ce ticket ? `parLeBureau` : le bureau, qui règle parfois un
 * problème par téléphone — il n'a alors ni partenaire ni photo à fournir.
 */
export function peutResoudre(
  ticket: Pick<Ticket, 'statut' | 'partenaireId'>,
  rapport: string,
  photos: number,
  parLeBureau: boolean
): Verdict {
  if (ticket.statut === 'resolu' || ticket.statut === 'clos') {
    return { ok: false, raison: 'Ce ticket est déjà résolu.' };
  }
  const manques: string[] = [];
  const texte = rapport.trim();
  if (texte.length < RAPPORT_MIN) manques.push('le rapport d’intervention');
  if (!parLeBureau) {
    if (ticket.partenaireId === null) {
      return { ok: false, raison: 'Ce ticket n’est confié à aucun partenaire.' };
    }
    if (photos < 1) manques.push('1 photo');
  }
  if (manques.length > 0) return { ok: false, raison: `Il manque : ${manques.join(', ')}.` };
  if (texte.length > RAPPORT_MAX) {
    return { ok: false, raison: 'Le rapport tient en 4 000 caractères au plus.' };
  }
  return { ok: true };
}

export function peutClore(ticket: Pick<Ticket, 'statut'>): Verdict {
  if (ticket.statut === 'clos') return { ok: false, raison: 'Ce ticket est déjà clos.' };
  if (ticket.statut !== 'resolu') {
    return { ok: false, raison: 'Seul un ticket résolu peut être clos.' };
  }
  return { ok: true };
}

export function peutRouvrirTicket(ticket: Pick<Ticket, 'statut'>): Verdict {
  if (ticket.statut !== 'resolu' && ticket.statut !== 'clos') {
    return { ok: false, raison: 'Seul un ticket résolu ou clos peut être rouvert.' };
  }
  return { ok: true };
}

/** « SAV-2026-004 » : l'année, puis un numéro sur trois chiffres qui suit le plus grand existant. */
export function prochaineReferenceSav(existantes: readonly string[], annee: number): string {
  const prefixe = `SAV-${annee}-`;
  const max = existantes
    .filter((r) => r.startsWith(prefixe))
    .map((r) => Number(r.slice(prefixe.length)))
    .filter((n) => Number.isFinite(n))
    .reduce((a, b) => Math.max(a, b), 0);
  return `${prefixe}${String(max + 1).padStart(3, '0')}`;
}

/** Les tickets dans l'ordre où les traiter : urgents d'abord, puis les plus anciens. */
export function trierTickets<T extends Pick<Ticket, 'priorite' | 'ouvertLe'>>(
  tickets: readonly T[]
): T[] {
  return [...tickets].sort(
    (a, b) =>
      Number(b.priorite === 'urgente') - Number(a.priorite === 'urgente') ||
      a.ouvertLe.localeCompare(b.ouvertLe)
  );
}

// ── La fiche transmise au fabricant (db/035-sav-fiche-fabricant.sql) ──────────

export type EtatPiece =
  'hors-service' | 'a-remplacer' | 'a-reparer' | 'fonctionne-mal' | 'endommagee' | 'a-verifier';

/** Dans l'ordre où on les propose : du plus grave au simple doute. */
export const ETATS_PIECE: readonly { id: EtatPiece; libelle: string; aide: string }[] = [
  { id: 'hors-service', libelle: 'Hors service', aide: 'Ne fonctionne plus du tout' },
  {
    id: 'a-remplacer',
    libelle: 'À remplacer',
    aide: 'Irréparable, une pièce neuve est nécessaire',
  },
  { id: 'a-reparer', libelle: 'À réparer', aide: 'Réparable sur place ou en atelier' },
  {
    id: 'fonctionne-mal',
    libelle: 'Fonctionne mal',
    aide: 'Bruit, panne par intermittence, rendement faible',
  },
  { id: 'endommagee', libelle: 'Endommagée', aide: 'Casse, choc, intempérie' },
  { id: 'a-verifier', libelle: 'À vérifier', aide: 'Doute, à diagnostiquer' },
];

export const LIBELLE_ETAT_PIECE: Record<EtatPiece, string> = Object.fromEntries(
  ETATS_PIECE.map((e) => [e.id, e.libelle])
) as Record<EtatPiece, string>;

export type ArretInstallation = 'oui' | 'partiel' | 'non';

export const LIBELLE_ARRET: Record<ArretInstallation, string> = {
  oui: 'Oui, à l’arrêt',
  partiel: 'En partie',
  non: 'Non, elle tourne',
};
